import { useCallback, useEffect, useContext } from 'react'
import { pipe, find, propEq, pathOr, reject, isEmpty } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import useMediaQuery from '@/hooks/use-media-query'
import { colors, breakpoints } from '@/styles'
import { select } from '@/store'
import Tables from './tables'
import DocumentsIcon from './documents-icon.svg'
import TenancyIcon from './tenancy-icon.svg'
import SupplementaryIcon from './supplementary-icon.svg'
import RedFolderIcon from './red-folder-icon.svg'
import AddDocumentButton from './add-document-button'
import { getFormattedPackageFiles, getFormattedSigners } from '../utils'
import Context from '../context'
import { updateAt } from '../utils'

const Container = styled.div`
  max-width: 1192px;
  margin: 34px 30px 80px;
`

const HeadingContainer = styled.div`
  display: flex;
  margin-bottom: 40px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 50px 3fr;
  width: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: block;
  }
`

const GridOne = styled.div`
  grid-column-start: 1;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const GridTwo = styled.div`
  grid-column-start: 2;
  max-width: 760px;
`

const VerticalLine = styled.span`
  display: block;
  width: 38px;
  margin-top: -10px;
  background: linear-gradient(${colors.grey88}, ${colors.grey88}) no-repeat center/2px 100%;
`

const VerticalLineOne = styled(VerticalLine)`
  height: 42px;
`

const VerticalLineTwo = styled(VerticalLine)`
  height: 148px;
`

const HeaderWrapper = styled.div`
  display: flex;
`

const AddressContainer = styled.div`
  display: flex;
  height: 20px;
  margin-top: 10px;
  margin-bottom: 54px;
`

const Heading = styled.h5`
  margin-left: 10px;
  font-size: 20px;
  font-weight: bold;
`

const DocumentIconImage = styled.img`
  height: 38px;
  width: 38px;
`

const TableTitle = styled.h5`
  font-size: 16px;
  font-weight: 500;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-bottom: 12px;
    margin-left: 10px;
    font-size: 20px;
    font-weight: bold;
  }
`

const TableSubtitle = styled.h5`
  font-size: 16px;
  font-weight: 500;
`

const RequiredAsterixHeader = styled(TableSubtitle)`
  color: ${colors.rainbowRed};
  margin-left: 4px;
`

const getTenancyPackageFile = pipe(find(propEq('document_type', 'tenancy_agreement')))
const getSupportingDocs = pipe(reject(propEq('document_type', 'tenancy_agreement')))

const Documents = ({ currentStep, handleValidate, contractId, setIsFilled, setSignees }) => {
  const { contract = {}, packageFiles = [] } = useSelector(state =>
    select.contract.contractPackageInfo(state, { contractId })
  )
  const userContext = useContext(Context)
  const { fileMetadata, setFileMetadata } = userContext
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const tenancyAgreementPackageFile = getTenancyPackageFile(packageFiles) ?? {}
  const supportingDocuments = getSupportingDocs(packageFiles) ?? {}
  const isContractSigned = tenancyAgreementPackageFile.state_machine === 'signed'
  const canUploadAdditionalFiles =
    tenancyAgreementPackageFile.state_machine !== 'sent' && !isContractSigned

  useEffect(() => {
    if (!isEmpty(tenancyAgreementPackageFile)) {
      handleValidate(true)
    }
  }, [tenancyAgreementPackageFile, handleValidate])
  // todo: refactor by currying these functions
  const handleUploadTenancyAgreement = useCallback(
    async (acceptedFiles, metadata) => {
      if (!contract?.id) return
      const file = acceptedFiles[0]
      if (file) {
        const { response, body } = await dispatch.contract.uploadCustomContract({
          file,
          contractId: contract?.id
        })
        if (response.ok) {
          const formattedFiles = getFormattedPackageFiles(body?.package_files)
          const formattedSigners = getFormattedSigners(body?.signers)
          setFileMetadata({
            ...metadata,
            current_document: body?.package_files?.[0]?.id,
            package_files: formattedFiles
          })
          setSignees(formattedSigners)
          handleValidate(true)
        }
      }
    },
    [contract?.id, dispatch.contract]
  )

  const handleUploadSuportingDoc = useCallback(
    async (acceptedFiles, metadata) => {
      if (!contract?.id) return

      if (!isEmpty(acceptedFiles)) {
        const { body } = await dispatch.contract.uploadSupportingDocument({
          files: acceptedFiles,
          contractId: contract?.id
        })
        const formattedFiles = getFormattedPackageFiles(body?.package_files)
        const formattedSigners = getFormattedSigners(body?.signers)

        setFileMetadata(updateAt(metadata, ['package_files'], formattedFiles))
        setSignees(formattedSigners)
      }
    },
    [contract?.id, dispatch.contract]
  )

  const handlePackageFileDelete = async fileId => {
    const { body } = await dispatch.contract.removePackageFile({
      contractId: contract?.id,
      fileId: fileId
    })

    const formattedFiles = getFormattedPackageFiles(body?.package_files)
    const formattedSigners = getFormattedSigners(body?.signers)
    const tenancyAgreement = pipe(
      pathOr([], ['package_files']),
      find(propEq('document_type', 'tenancy_agreement'))
    )(body)

    const updatedMetaData = updateAt(fileMetadata, ['package_files'], formattedFiles)

    setFileMetadata(updateAt(updatedMetaData, ['current_document'], tenancyAgreement?.id))
    setSignees(formattedSigners)
  }

  const handleCancelContract = async () => {
    const { response } = await dispatch.contract.removeTenancyAgreement({
      contractId: contract?.id,
      fileId: tenancyAgreementPackageFile?.id
    })
    if (response.ok) setIsFilled(false)
  }

  return (
    <Container>
      <HeadingContainer>
        <Heading>
          {t('l.contract_edit.add_recipients.add_documents.title', 'Add Documents')}
        </Heading>
      </HeadingContainer>
      <GridContainer>
        <GridOne>
          <DocumentIconImage src={DocumentsIcon} alt="documents" />
          <VerticalLineOne />
          <img src={TenancyIcon} alt="tenancy" />

          <VerticalLineTwo />
          <img src={SupplementaryIcon} alt="supplementary" />
        </GridOne>
        <GridTwo>
          <HeaderWrapper>
            <AddressContainer>
              {isMobile && <img src={RedFolderIcon} alt="icon" />}
              <TableTitle>
                {contract?.unit?.unit_number ? contract?.unit?.unit_number + ' - ' : ''}
                {contract?.building?.full_address}
              </TableTitle>
            </AddressContainer>
          </HeaderWrapper>
          <HeaderWrapper>
            <TableSubtitle>
              {t('l.contract_manager.documents.tenancy_agreement.title', 'Tenancy Agreement')}
            </TableSubtitle>
            <RequiredAsterixHeader>*</RequiredAsterixHeader>
          </HeaderWrapper>
          <Tables
            header
            primary
            contract={contract}
            isMobile={isMobile}
            packageFile={tenancyAgreementPackageFile}
            handleUploadFile={e => handleUploadTenancyAgreement(e, fileMetadata)}
            handleDelete={handleCancelContract}
          />
          <TableSubtitle>
            {t(
              'l.contract_manager.documents.supplementary_documents.title',
              'Supplementary Documents'
            )}
          </TableSubtitle>
          {supportingDocuments.map((document, index) => (
            <Tables
              index={index}
              key={index}
              header={index === 0}
              secondary
              contract={contract}
              packageFile={document}
              handleUploadFile={e => handleUploadSuportingDoc(e, fileMetadata)}
              handleDelete={() => handlePackageFileDelete(document?.id)}
            />
          ))}

          {canUploadAdditionalFiles && (
            <AddDocumentButton
              handleUploadFile={e => handleUploadSuportingDoc(e, fileMetadata)}
              isPrimary={false}
            />
          )}
        </GridTwo>
      </GridContainer>
    </Container>
  )
}

export default Documents
